import {
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  Table,
  UsePrivileges,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { Box, Chip, Stack } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { format } from "date-fns";
import { IGetAllNotifications } from "../../../../models/notifications";
import MainApiService from "../../../../services/mainApi.service";

import GroupIcon from "@mui/icons-material/Group";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useCallback, useMemo, useState } from "react";
import {
  cancelNotificationBatch,
  generateNotificationBatch,
} from "../../../../services/notification.service";

const NOTIFICATIONS_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/Notification/";
const notificationsService = new MainApiService<IGetAllNotifications>(
  NOTIFICATIONS_ROUTE
);

const translationPath = "page.register.notifications.";

const NotificationsPage = () => {
  const navigate = useNavigate();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [enableFields, setEnableFields] = useState<boolean>(true);
  const [selectedFilter, setSelectedFilter] = useState<ISelectType | null>(
    null
  );
  const filterOptions: ISelectType[] = useMemo(
    () => [
      {
        id: "startPublish",
        name: t(translationPath + "start_publish") || "Start Of Publication",
        value: "startPublish",
      },
      {
        id: "finishPublish",
        name: t(translationPath + "finish_publish") || "End Of Publication",
        value: "finishPublish",
      },
    ],
    []
  );

  const { privileges } = UsePrivileges();
  const batchPrivilege =
    !!privileges.find((p) => p.domainName === "notificationadm") ||
    !!privileges.find((p) => p.domainName === "all");

  const { setMessage } = useNotificationContext();

  const [render, setRender] = useState(0);

  const { sendRequest: generateBatch, loading: gLoading } = useFetch(
    generateNotificationBatch
  );

  const { sendRequest: cancelBatch, loading: cLoading } = useFetch(
    cancelNotificationBatch
  );

  const onRefreshHandler = useCallback(() => {
    setRender((prevState) => (prevState = prevState + 1));
  }, []);

  const columns: GridColDef<IGetAllNotifications>[] = [
    {
      field: "title",
      headerName: t(translationPath + "title") || "Title",
      renderCell: (params) =>
        params.row.notificationBatchContents.find(
          (content) => content.language.code === language
        )?.title || "-",
    },
    {
      field: "startPublish",
      headerName: t(translationPath + "start_publish") || "Start publish",
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P pp", {
              locale: language === "pt-BR" ? ptBR : enUS,
            }).slice(0, -3)
          : "-";
      },
    },
    {
      field: "finishPublish",
      headerName: t(translationPath + "finish_publish") || "Finish publish",
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P pp", {
              locale: language === "pt-BR" ? ptBR : enUS,
            }).slice(0, -3)
          : "-";
      },
    },
    {
      field: "type",
      headerName: t(translationPath + "type") || "Type",
      renderCell: (params) => {
        return params.value.name;
      },
    },
    {
      field: "isPublished",
      headerName: t(translationPath + "published_status") || "Published status",
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value ? "success" : "error"}
          label={
            params.value
              ? t(translationPath + "published")
              : t(translationPath + "not_published")
          }
        />
      ),
    },
    {
      field: "status",
      headerName: t(translationPath + "status") || "Notification status",
      renderCell: (params) => (
        <Chip
          size="small"
          color={
            params.value.code === "1"
              ? "primary"
              : params.value.code === "2"
              ? "default"
              : params.value.code === "3"
              ? "success"
              : "error"
          }
          label={
            params.value.code === "1"
              ? t(translationPath + "registered")
              : params.value.code === "2"
              ? t(translationPath + "generating")
              : params.value.code === "3"
              ? t(translationPath + "generated")
              : params.value.code === "4"
              ? t(translationPath + "canceled")
              : t(translationPath + "error")
          }
        />
      ),
    },
    {
      field: "enable",
      headerName: t(translationPath + "active_status") || "Status",
      maxWidth: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value ? "success" : "error"}
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
        />
      ),
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IGetAllNotifications>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const generateBatchHandler = useCallback(
    async (id: string) => {
      if (id) {
        const { data, success } = await generateBatch(id);
        if (data && success) {
          onRefreshHandler();
          setMessage({
            message: `${t(
              "components.notificationBatchActions.batch_generated"
            )}`,
            type: "success",
          });
        }
      }
    },
    [generateBatch, setMessage, t, onRefreshHandler]
  );

  const cancelBatchHandler = useCallback(
    async (id: string) => {
      if (id) {
        const { data, success } = await cancelBatch(id);
        if (data && success) {
          onRefreshHandler();
          setMessage({
            message: `${t(
              "components.notificationBatchActions.batch_canceled"
            )}`,
            type: "success",
          });
        }
      }
    },
    [cancelBatch, setMessage, t, onRefreshHandler]
  );

  const viewNotifiedUsersHandler = useCallback(
    (id: string) => {
      navigate(`users/${id}`);
    },
    [navigate]
  );

  const loading = gLoading || cLoading;

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
      TypeFilter:
        selectedFilter && selectedFilter.id == "startPublish"
          ? "start"
          : selectedFilter && selectedFilter.id == "finishPublish"
          ? "end"
          : undefined,
      render: render,
    };
  }, [render, startDate, endDate, selectedFilter]);

  return (
    <Table<IGetAllNotifications>
      showDefaultMacroFilters={false}
      service={notificationsService}
      columns={columns}
      title={t(translationPath + "notifications")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      syncing={loading}
      serviceProps={props}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
                showFilter={true}
                filterOptions={filterOptions}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
              />
            </Box>

            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      rowActions={(params) => {
        let actions = [
          {
            action: () => viewNotifiedUsersHandler(params.row.id),
            icon: <GroupIcon />,
            text: t("components.notificationBatchActions.users"),
            divider: true,
          },
        ];

        const baseValidation = params.row.status.code !== "4" && batchPrivilege;

        const notificationTypeCode = params.row.type.code;

        if (
          baseValidation &&
          params.row.finishPublish &&
          (notificationTypeCode === "1" || notificationTypeCode === "3")
        ) {
          actions.push({
            action: () => generateBatchHandler(params.row.id),
            icon: <NotificationsIcon />,
            text: t("components.notificationBatchActions.generate"),
            divider: true,
          });
        }

        if (baseValidation) {
          actions.push({
            action: () => cancelBatchHandler(params.row.id),
            icon: <NotificationsOffIcon />,
            text: t("components.notificationBatchActions.cancel"),
            divider:
              notificationTypeCode !== "1" && notificationTypeCode !== "3",
          });
        }

        return actions;
      }}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a Notification"
      }
      addButtonLabel={
        t(translationPath + "add_notification") || "Add Notification"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default NotificationsPage;
